import React from 'react';
import { type EmailTemplate } from '@/hooks/useEmailTemplates';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

interface EmailTemplatePreviewModalProps {
  template: EmailTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EmailTemplatePreviewModal: React.FC<EmailTemplatePreviewModalProps> = ({ 
  template, 
  isOpen, 
  onClose 
}) => {
  const { toast } = useToast();

  if (!template) return null;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(template.html_content);
    toast({
      title: 'Código copiado',
      description: 'O código HTML foi copiado para a área de transferência',
    });
  };

  const getCategoryLabel = (category: string) => {
    return category === 'authentication' ? 'Autenticação' : 'Notificação';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] bg-gradient-to-br from-black via-gray-900 to-black border-white/10">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span>{template.name}</span>
              {template.is_active && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">ATIVO</Badge>
              )}
              {template.is_draft && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">DRAFT</Badge>
              )}
              <Badge variant="outline" className="bg-white/5 border-white/10 text-white/60">
                v{template.version}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyCode}
              className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar HTML
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Template Info */}
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              {getCategoryLabel(template.category)}
            </Badge>
            <Badge variant="outline" className="bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30">
              {template.supabase_type}
            </Badge>
          </div>

          {template.description && (
            <p className="text-sm text-white/60">{template.description}</p>
          )}

          {/* Preview */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 overflow-auto max-h-[50vh]">
            <iframe
              srcDoc={template.html_content}
              title={`Preview: ${template.name}`}
              className="w-full h-[600px] bg-white rounded border border-white/10"
              sandbox="allow-same-origin"
            />
          </div>

          {/* Variables */}
          <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
            <h3 className="text-sm font-semibold text-white mb-2">📋 Variáveis Disponíveis</h3>
            <p className="text-xs text-white/60 mb-2">Estas variáveis são substituídas automaticamente pelo Supabase Auth:</p>
            <div className="flex flex-wrap gap-2">
              {template.variables.map((variable: string) => (
                <code key={variable} className="text-xs bg-black/50 px-3 py-1.5 rounded border border-white/20 text-luxury-pinkBright">
                  {variable}
                </code>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
